/**
 * Чистые помощники экрана агроцикла: подписи и раскладка готовых ответов.
 *
 * Чисел здесь не считают: доли, участники, земля и «впереди» приходят с
 * сервера (слой метрик). Здесь — только слова вокруг них и одно сопоставление:
 * кампания хронологии → этап цикла (этап — строка цикла, а не группировка
 * справочника стадий).
 */

import { defineMessages } from '../../../i18n/define';
import type { Message } from '../../../i18n/define';
import { tr, numberLocale } from '../../../i18n/runtime';
import { WORDS } from '../../lib/format';
import type { AgrocycleDetail, Kpi, PlanStage, PlanWork, Source, StageProgress } from './agrocycleApi';

const m = defineMessages({
  planNone: {
    id: 'cycle.agrocycle.plan.none',
    defaultMessage: 'план не посчитан',
    description: 'Строка про план агроцикла (хоким, менеджер): план ещё не посчитан сервером',
  },
  planLine: {
    id: 'cycle.agrocycle.plan.line',
    defaultMessage:
      'запущено {launched} из {total, plural, one {# работы плана} other {# работ плана}} · впереди {upcoming}',
    description:
      'Строка про план агроцикла под названием: {launched} — сколько работ плана запущено (число, строкой), {total} — всего работ плана (число), {upcoming} — сколько работ впереди: все работы плана без кампании, total − launched (число, строкой)',
  },
  whoLine: {
    id: 'cycle.agrocycle.who.line',
    defaultMessage: '{farms} · {land} га земли',
    description:
      'Кто участвует в агроцикле: {farms} — «24 хозяйства» (число с словом), {land} — уникальная земля в гектарах, число строкой; «га земли» отличается от «га работ»',
  },
  doneNone: {
    id: 'cycle.agrocycle.done.none',
    defaultMessage: 'кампаний ещё не запускали',
    description: 'Строка про ход работ агроцикла: по агроциклу ещё не запускали ни одной кампании',
  },
  doneLine: {
    id: 'cycle.agrocycle.done.line',
    defaultMessage: '{area} % площади работ · {count} % по числу работ',
    description: 'Ход работ агроцикла: пара долей; {area} — доля по площади работ, {count} — доля по числу работ, числа строкой',
  },
  sourceNone: {
    id: 'cycle.agrocycle.source.none',
    defaultMessage: 'записей нет',
    description: 'Строка источника работ агроцикла (план, предписания, добровольные): записей нет',
  },
  sourceVoluntaryCount: {
    id: 'cycle.agrocycle.source.voluntaryCount',
    defaultMessage: '{count, plural, one {# запись} few {# записи} many {# записей} other {# записи}}',
    description: 'Число добровольных работ хозяйств в строке источника; {count} — число',
  },
  sourceAreaNone: {
    id: 'cycle.agrocycle.source.areaNone',
    defaultMessage: 'площадь не указана',
    description: 'Фрагмент строки источника или этапа: площадь работ не указана',
  },
  sourceArea: {
    id: 'cycle.agrocycle.source.area',
    defaultMessage: '{value} га работ',
    description: 'Фрагмент строки источника: площадь работ в гектарах; {value} — число строкой; «га работ» — сумма по работам на полях, не уникальная земля',
  },
  sourceOutside: {
    id: 'cycle.agrocycle.source.outside',
    defaultMessage: 'вне % выполнения',
    description: 'Фрагмент строки добровольных работ: они не входят в процент выполнения плана',
  },
  sourceDone: {
    id: 'cycle.agrocycle.source.done',
    defaultMessage: 'выполнено {area} % площади',
    description: 'Фрагмент строки источника: доля выполнения по площади; {area} — число строкой',
  },
  sourceDoneBoth: {
    id: 'cycle.agrocycle.source.doneBoth',
    defaultMessage: 'выполнено {area} % площади ({count} % по числу работ)',
    description: 'Фрагмент строки источника: доля по площади и в скобках по числу работ; числа строкой',
  },
  stageDoneArea: {
    id: 'cycle.agrocycle.stage.doneArea',
    defaultMessage: 'выполнено {value} % площади',
    description: 'Ход этапа агроцикла: доля выполнения по площади работ; {value} — число строкой',
  },
  stageCount: {
    id: 'cycle.agrocycle.stage.count',
    defaultMessage: '{value} % по числу работ',
    description: 'Ход этапа агроцикла: доля выполнения по числу работ; {value} — число строкой',
  },
  stageBaseHa: {
    id: 'cycle.agrocycle.stage.baseHa',
    defaultMessage: 'из {value} га работ',
    description: 'Ход этапа: знаменатель — сумма площади работ на полях; {value} — число строкой',
  },
  stageBaseWorks: {
    id: 'cycle.agrocycle.stage.baseWorks',
    defaultMessage: 'из {count, plural, one {# работы на поле} other {# работ на полях}}',
    description: 'Ход этапа: знаменатель — число работ на полях, когда площадь не известна; {count} — число',
  },
  noDate: {
    id: 'cycle.agrocycle.window.noDate',
    defaultMessage: 'без даты',
    description: 'Окно работы плана агроцикла: даты не назначены',
  },
  stateAhead: {
    id: 'cycle.agrocycle.workState.ahead',
    defaultMessage: 'впереди',
    description: 'Состояние работы плана: окно ещё впереди, кампания не запущена; бейдж',
  },
  stateLaunched: {
    id: 'cycle.agrocycle.workState.launched',
    defaultMessage: 'запущена',
    description: 'Состояние работы плана: кампания запущена; бейдж',
  },
  stateCancelled: {
    id: 'cycle.agrocycle.workState.cancelled',
    defaultMessage: 'отменена',
    description: 'Состояние работы плана: работа отменена; бейдж',
  },
  statusNotStarted: {
    id: 'cycle.agrocycle.status.notStarted',
    defaultMessage: 'не начат',
    description: 'Статус агроцикла: ещё не начался; бейдж в строке агроцикла и подпись в шапке',
  },
  statusStarted: {
    id: 'cycle.agrocycle.status.started',
    defaultMessage: 'идёт',
    description: 'Статус агроцикла: идёт сейчас; бейдж в строке агроцикла и подпись в шапке',
  },
  statusFinished: {
    id: 'cycle.agrocycle.status.finished',
    defaultMessage: 'завершён',
    description: 'Статус агроцикла: завершён; бейдж в строке агроцикла и подпись в шапке',
  },
  warnNoFarm: {
    id: 'cycle.agrocycle.warning.noFarm',
    defaultMessage: 'нет хозяйства в размещении',
    description: 'Конфликт размещения агроцикла (менеджер): в размещении контура не указано хозяйство',
  },
  warnNoLandUse: {
    id: 'cycle.agrocycle.warning.noLandUse',
    defaultMessage: 'нет землепользования на дату',
    description: 'Конфликт размещения агроцикла (менеджер): на дату нет записи о землепользовании',
  },
  warnNotLandUser: {
    id: 'cycle.agrocycle.warning.notLandUser',
    defaultMessage: 'хозяйство размещения ≠ землепользователь',
    description: 'Конфликт размещения агроцикла (менеджер): хозяйство из размещения не совпадает с землепользователем',
  },
});

export function num(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat(numberLocale(), { maximumFractionDigits: 1 }).format(value);
}

export function kpi(kpis: Record<string, Kpi> | undefined, id: string): number | null {
  const value = kpis?.[id]?.value;
  return typeof value === 'number' ? value : null;
}

/**
 * «Впереди» — ВСЕ работы плана без кампании: M − K (`plan.works` −
 * `plan.launched`), журнал №1524, №1650. Одна функция для шапки цикла, хаба,
 * обзора района и кабинета хозяйства. Не `plan.upcoming`: та метрика — блок
 * «Скоро» (окно в ближайшие 30 дней), и обзор с ней читал «впереди 1» при
 * «38 из 40 · впереди 2» на странице цикла.
 */
export function planAhead(kpis: Record<string, Kpi> | undefined): number | null {
  const total = kpi(kpis, 'plan.works');
  const launched = kpi(kpis, 'plan.launched');
  return total === null || launched === null ? null : Math.max(0, total - launched);
}

/** «запущено K из M работ плана · впереди N» (design §7.1 п. 6). */
export function planLine(kpis: Record<string, Kpi> | undefined): string {
  const total = kpi(kpis, 'plan.works');
  if (total === null) return tr(m.planNone);
  const launched = kpi(kpis, 'plan.launched') ?? 0;
  const ahead = planAhead(kpis) ?? Math.max(0, total - launched);
  return tr(m.planLine, { launched: num(launched), total, upcoming: num(ahead) });
}

/** «24 хозяйства · 370 га земли» — хозяйства всегда с гектарами (№7.1 п. 5). */
export function whoLine(kpis: Record<string, Kpi> | undefined): string {
  const farms = kpi(kpis, 'cycle.farms');
  const land = kpi(kpis, 'cycle.land_ha');
  return tr(m.whoLine, { farms: farms === null ? '—' : tr(WORDS.farm, { count: farms }), land: num(land) });
}

/** «78 % площади работ · 80 % по числу работ» — пара хода работ. */
export function doneLine(kpis: Record<string, Kpi> | undefined): string {
  const area = kpi(kpis, 'area.done_pct');
  const count = kpi(kpis, 'reports.done_pct');
  if (area === null && count === null) return tr(m.doneNone);
  return tr(m.doneLine, { area: num(area), count: num(count) });
}

const SEP = ' · ';

/**
 * Строка источника работ (план · предписания · добровольные, design §7.1 п. 6):
 * основа и «из N» словами. Пустое число сервера — «записей нет», а не ноль.
 * Добровольные — без доли выполнения: это не обязательства (№794).
 */
export function sourceLine(row: Source): string {
  if (row.works === null || row.works === undefined) return tr(m.sourceNone);
  const head = row.source === 'voluntary'
    ? tr(m.sourceVoluntaryCount, { count: row.works })
    : tr(WORDS.obligation, { count: row.works });
  const area = row.area_ha === null || row.area_ha === undefined ? tr(m.sourceAreaNone) : tr(m.sourceArea, { value: num(row.area_ha) });
  if (row.source === 'voluntary') return [head, area, tr(m.sourceOutside)].join(SEP);
  const parts = [head, area];
  if (row.done_pct !== null && row.done_pct !== undefined) {
    parts.push(
      row.count_done_pct === null || row.count_done_pct === undefined
        ? tr(m.sourceDone, { area: num(row.done_pct) })
        : tr(m.sourceDoneBoth, { area: num(row.done_pct), count: num(row.count_done_pct) }),
    );
  }
  return parts.join(SEP);
}

/** Строка хода этапа: «82 % площади · 80 % по числу работ · из 66 480 га работ». */
export function stageProgressLine(row: StageProgress | undefined): string | null {
  if (!row || row.works === null || row.works === undefined) return null;
  const parts = [
    row.done_pct === null || row.done_pct === undefined ? tr(m.sourceAreaNone) : tr(m.stageDoneArea, { value: num(row.done_pct) }),
  ];
  if (row.count_done_pct !== null && row.count_done_pct !== undefined) parts.push(tr(m.stageCount, { value: num(row.count_done_pct) }));
  parts.push(
    row.work_ha === null || row.work_ha === undefined
      ? tr(m.stageBaseWorks, { count: row.works })
      : tr(m.stageBaseHa, { value: num(Math.round(row.work_ha)) }),
  );
  return parts.join(SEP);
}

function dm(iso: string): string {
  const [, month, d] = iso.split('-');
  return `${d}.${month}`;
}

/** Окно работы словами; «≈» — срок примерный (просрочки по нему нет). */
export function windowText(work: Pick<PlanWork, 'window_from' | 'window_to' | 'approximate'>): string {
  if (!work.window_from) return tr(m.noDate);
  const body = work.window_to && work.window_to !== work.window_from
    ? `${dm(work.window_from)}–${dm(work.window_to)}`
    : dm(work.window_from);
  return work.approximate ? `≈ ${body}` : body;
}

const STATE_MESSAGE: Record<PlanWork['state'], Message> = {
  ahead: m.stateAhead,
  launched: m.stateLaunched,
  cancelled: m.stateCancelled,
};

export function stateLabel(state: PlanWork['state']): string {
  return tr(STATE_MESSAGE[state]);
}

const STATUS_MESSAGE: Record<AgrocycleDetail['status'], Message> = {
  not_started: m.statusNotStarted,
  started: m.statusStarted,
  finished: m.statusFinished,
};

export function statusLabel(status: AgrocycleDetail['status']): string {
  return tr(STATUS_MESSAGE[status]);
}

const WARNING_MESSAGE: Record<string, Message> = {
  no_farm: m.warnNoFarm,
  no_land_use: m.warnNoLandUse,
  farm_not_land_user: m.warnNotLandUser,
};

export function warningLabel(reason: string): string {
  const message = WARNING_MESSAGE[reason];
  return message ? tr(message) : reason;
}

/** Конфликты размещения по причинам: «3 · хозяйство размещения ≠ …». */
export function warningSummary(warnings: ReadonlyArray<{ reason: string }>): { reason: string; label: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const w of warnings) counts.set(w.reason, (counts.get(w.reason) ?? 0) + 1);
  return [...counts.entries()].map(([reason, count]) => ({
    reason,
    label: warningLabel(reason),
    count,
  }));
}

export interface StageOfCampaign {
  name: string;
  order: number;
}

/** Кампания → этап цикла (строка), по работам плана с кампанией. */
export function stagesByCampaign(stages: ReadonlyArray<PlanStage> | undefined): Map<string, StageOfCampaign> {
  const out = new Map<string, StageOfCampaign>();
  for (const stage of stages ?? []) {
    for (const work of stage.works) {
      if (work.campaign_id !== null && work.campaign_id !== undefined) {
        out.set(String(work.campaign_id), { name: stage.name, order: stage.order_no });
      }
    }
  }
  return out;
}

/**
 * Работы хронологии с этапом цикла вместо стадии справочника: этап — строка
 * шаблона/цикла. Работа без пары (переходный срок) оставляет свою стадию и
 * идёт после этапов цикла. Порядок — этап, затем прежний порядок работы.
 */
export function withCycleStages<T extends { id: string; stage: string; seq: number }>(
  works: ReadonlyArray<T>,
  map: Map<string, StageOfCampaign>,
): T[] {
  if (map.size === 0) return [...works];
  return works
    .map((work) => {
      const found = map.get(work.id);
      return { work: found ? { ...work, stage: found.name } : work, order: found?.order ?? Number.MAX_SAFE_INTEGER };
    })
    .sort((a, b) => a.order - b.order || a.work.seq - b.work.seq)
    .map((row) => row.work);
}
